import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Plus, PencilSimple, Trash, Check, X, Star } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const empty = () => ({ name: "", role: "", quote: "", rating: 5, avatar_url: "", approved: true, order: 0 });

export default function AdminTestimonials() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | "new" | id
  const [draft, setDraft] = useState(empty());
  const [saving, setSaving] = useState(false);

  const isAdmin = user?.role === "admin";

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/testimonials/admin/all");
      setItems(r.data?.items || []);
    } catch {
      toast.error("Failed to load testimonials");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { refresh(); }, []);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-app text-primary-fg flex items-center justify-center">
        <div className="text-sm text-muted-fg">Admin only.</div>
      </div>
    );
  }

  const startEdit = (it) => {
    setEditing(it.id);
    setDraft({
      name: it.name || "",
      role: it.role || "",
      quote: it.quote || "",
      rating: it.rating || 5,
      avatar_url: it.avatar_url || "",
      approved: it.approved,
      order: it.order || 0,
    });
  };
  const startNew = () => { setEditing("new"); setDraft(empty()); };
  const cancel = () => { setEditing(null); setDraft(empty()); };

  const save = async () => {
    if (draft.name.length < 2) return toast.error("Name required");
    if (draft.quote.length < 20) return toast.error("Quote needs at least 20 chars");
    setSaving(true);
    try {
      const payload = { ...draft, role: draft.role || null, avatar_url: draft.avatar_url || null };
      if (editing === "new") {
        await api.post("/testimonials", payload);
        toast.success("Testimonial added");
      } else {
        await api.put(`/testimonials/${editing}`, payload);
        toast.success("Testimonial updated");
      }
      await refresh();
      cancel();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    try {
      await api.delete(`/testimonials/${id}`);
      toast.success("Deleted");
      refresh();
    } catch { toast.error("Delete failed"); }
  };

  return (
    <div className="min-h-screen bg-app text-primary-fg">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <Link to="/admin" className="inline-flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-widest text-muted-fg hover:text-primary-fg transition mb-5">
          <ArrowLeft size={11} weight="bold" />
          Back to Admin
        </Link>

        <div className="flex items-center justify-between mb-7">
          <div>
            <h1 className="text-2xl sm:text-3xl font-medium tracking-tight">Testimonials · Admin</h1>
            <p className="text-[11px] text-muted-fg mt-1 max-w-xl">
              Add <strong>real</strong> user reviews collected via Feedback page, Telegram channel,
              or email. Per ASCI guidelines, fabricated testimonials are prohibited in India.
            </p>
          </div>
          {!editing && (
            <button
              type="button" onClick={startNew} data-testid="admin-testimonial-new"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-sm text-xs font-medium"
              style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}
            >
              <Plus size={12} weight="bold" /> Add Testimonial
            </button>
          )}
        </div>

        {editing ? (
          <div className="border border-subtle rounded-sm p-5 bg-surface/40 space-y-3" data-testid="admin-testimonial-editor">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">Name</label>
                <input value={draft.name} onChange={e => setDraft({...draft, name: e.target.value})}
                  className="w-full bg-app border border-subtle rounded-sm px-3 py-2 text-sm" data-testid="admin-testimonial-name" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">Role / Location</label>
                <input value={draft.role} onChange={e => setDraft({...draft, role: e.target.value})}
                  placeholder="Swing trader, Pune" className="w-full bg-app border border-subtle rounded-sm px-3 py-2 text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">Quote</label>
              <textarea rows={3} value={draft.quote} onChange={e => setDraft({...draft, quote: e.target.value})}
                className="w-full bg-app border border-subtle rounded-sm px-3 py-2 text-sm" data-testid="admin-testimonial-quote" />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">Rating (1-5)</label>
                <input type="number" min={1} max={5} value={draft.rating}
                  onChange={e => setDraft({...draft, rating: Math.min(5, Math.max(1, parseInt(e.target.value)||5))})}
                  className="w-full bg-app border border-subtle rounded-sm px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">Order (higher = first)</label>
                <input type="number" value={draft.order} onChange={e => setDraft({...draft, order: parseInt(e.target.value)||0})}
                  className="w-full bg-app border border-subtle rounded-sm px-3 py-2 text-sm font-mono" />
              </div>
              <div>
                <label className="block text-[10px] font-mono uppercase tracking-widest text-muted-fg mb-1.5">Avatar URL (optional)</label>
                <input value={draft.avatar_url} onChange={e => setDraft({...draft, avatar_url: e.target.value})}
                  placeholder="https://…" className="w-full bg-app border border-subtle rounded-sm px-3 py-2 text-sm" />
              </div>
            </div>
            <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={draft.approved} onChange={e => setDraft({...draft, approved: e.target.checked})} />
              Approved (visible on landing)
            </label>
            <div className="flex items-center gap-2 pt-2">
              <button type="button" onClick={save} disabled={saving} data-testid="admin-testimonial-save"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-medium disabled:opacity-50"
                style={{ background: "hsl(var(--pos))", color: "hsl(var(--bg))" }}>
                <Check size={12} weight="bold" />
                {saving ? "Saving…" : (editing === "new" ? "Add" : "Save")}
              </button>
              <button type="button" onClick={cancel}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-sm text-xs font-medium border border-subtle text-secondary-fg hover:bg-surface">
                <X size={12} weight="bold" /> Cancel
              </button>
            </div>
          </div>
        ) : (
          <div className="border border-subtle rounded-sm overflow-hidden">
            {loading ? (
              <div className="p-5 text-xs font-mono text-muted-fg animate-pulse">/// Loading…</div>
            ) : items.length === 0 ? (
              <div className="p-8 text-center">
                <div className="text-sm text-muted-fg mb-2">No testimonials yet.</div>
                <div className="text-[11px] text-muted-fg/70 max-w-md mx-auto">
                  Add real user reviews you've collected. Show in landing page only after approval.
                  Aim for 4-6 genuine reviews from active users.
                </div>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead><tr className="border-b border-subtle bg-surface/60">
                  {["User", "Quote", "Rating", "Order", "Status", "Actions"].map(h => (
                    <th key={h} className="text-left px-4 py-3 text-[10px] font-mono uppercase tracking-widest text-muted-fg">{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {items.map(it => (
                    <tr key={it.id} className="border-b border-subtle/50 hover:bg-surface/40">
                      <td className="px-4 py-3">
                        <div className="text-primary-fg">{it.name}</div>
                        <div className="text-[10px] text-muted-fg">{it.role}</div>
                      </td>
                      <td className="px-4 py-3 text-secondary-fg italic max-w-md truncate">{it.quote}</td>
                      <td className="px-4 py-3 text-mod tabular-nums"><Star size={11} weight="fill" className="inline mr-0.5"/> {it.rating}</td>
                      <td className="px-4 py-3 tabular-nums text-muted-fg">{it.order}</td>
                      <td className="px-4 py-3 text-[11px] font-mono">
                        {it.approved ? <span className="text-pos">Live</span> : <span className="text-muted-fg">Hidden</span>}
                      </td>
                      <td className="px-4 py-3 space-x-1">
                        <button type="button" onClick={() => startEdit(it)} className="p-1.5 text-secondary-fg hover:text-pos rounded-sm" aria-label="Edit">
                          <PencilSimple size={13} />
                        </button>
                        <button type="button" onClick={() => remove(it.id)} className="p-1.5 text-secondary-fg hover:text-neg rounded-sm" aria-label="Delete">
                          <Trash size={13} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
